/* ==========================================================================
   AURA — FAQ CATEGORY NAV
   --------------------------------------------------------------------------
   Smooth scroll + active-category highlighting for anchor links that point at
   FAQ category groups.

   Binds to ANY element carrying [data-faq-nav-link] with an in-page href, so
   the sticky desktop rail and the category cards at the top of the page share
   one implementation and one active state.

   Scroll position is observed with IntersectionObserver rather than a scroll
   handler — no per-frame work, nothing to throttle.
   ========================================================================== */

(function () {
  'use strict';

  function init() {
    const links = Array.from(document.querySelectorAll('[data-faq-nav-link]'))
      .filter(link => (link.getAttribute('href') || '').startsWith('#'));

    if (!links.length) return;

    /* One id can be targeted by several links (rail + card), so map id -> links. */
    const linksById = new Map();
    const targets = [];

    links.forEach(link => {
      const id = decodeURIComponent(link.getAttribute('href').slice(1));
      if (!id) return;

      const target = document.getElementById(id);
      if (!target) return;

      if (!linksById.has(id)) {
        linksById.set(id, []);
        targets.push(target);
      }
      linksById.get(id).push(link);
    });

    if (!targets.length) return;

    bindSmoothScroll(links);
    observe(targets, linksById);
  }

  /* ---- Smooth scroll ---------------------------------------------------- */

  function bindSmoothScroll(links) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    links.forEach(link => {
      if (link.dataset.auraBound) return;
      link.dataset.auraBound = '1';

      link.addEventListener('click', event => {
        const id = decodeURIComponent(link.getAttribute('href').slice(1));
        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();

        target.scrollIntoView({
          behavior: reduced ? 'auto' : 'smooth',
          block: 'start'
        });

        /* Keep the URL shareable and the back button meaningful, without the
           instant jump that setting location.hash would cause. */
        history.replaceState(null, '', '#' + id);

        /* Scrolling alone doesn't move focus, so keyboard users would carry on
           tabbing from the nav. Hand focus to the group without a second jump. */
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      });
    });
  }

  /* ---- Active highlighting ---------------------------------------------- */

  function observe(targets, linksById) {
    let activeId = null;

    const setActive = id => {
      if (id === activeId) return;
      activeId = id;

      linksById.forEach((group, key) => {
        const on = key === id;
        group.forEach(link => {
          link.classList.toggle('is-active', on);
          if (on) {
            link.setAttribute('aria-current', 'true');
          } else {
            link.removeAttribute('aria-current');
          }
        });
      });
    };

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) entry.target.dataset.faqVisible = '1';
          else delete entry.target.dataset.faqVisible;
        });

        /* Highlight the topmost group currently in the band. Recomputed from
           all targets rather than from `entries` alone, because a scroll can
           report only the group that left the band. */
        const visible = targets
          .filter(t => t.dataset.faqVisible)
          .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top);

        if (visible.length) setActive(visible[0].id);
      },
      {
        /* Band across the upper-middle of the viewport: a group counts as
           "current" once its heading passes the top area, and stops counting
           well before it leaves the screen. */
        rootMargin: '-96px 0px -55% 0px',
        threshold: 0
      }
    );

    targets.forEach(target => observer.observe(target));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Re-bindable after a Theme Editor section re-render. */
  window.AuraFaqNav = { init: init };

  document.addEventListener('shopify:section:load', init);
})();
